import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import Card from '@/components/Card'
import { Warning } from '@/components/Icons'
import Page from '@/components/Page'
import { getCurrentSpace, isRequestError, isRequestPending } from '@/store/selectors'

import {
  fetchActiveNowMetrics,
  fetchChatMetrics,
  fetchConnectorActivityMetrics,
  fetchEngagementAnalyticsMetrics,
  fetchSystemConnectorsMetrics
} from './actions'
import {
  FETCH_ACTIVE_NOW,
  FETCH_CHAT_ACTIVITY,
  FETCH_CONNECTOR_ACTIVITY,
  FETCH_SYSTEM_CONNECTOR_ACTIVITY
} from './actionTypes'
import {
  getChatMetrics,
  getConnectorActivityMetrics,
  getDeviceMetrics,
  getEngagementOverTimeMetrics,
  getPreviousChatMetrics,
  getPreviousPublisherMetrics,
  getPreviousTotalActiveUsers,
  getPreviousTotalActivity,
  getPreviousTotalEngagedUsers,
  getPreviousTotalEngagements,
  getPreviousTotalSpaceMembers,
  getPublisherMetrics,
  getTotalActiveUsers,
  getTotalActivity,
  getTotalEngagedNow,
  getTotalEngagedUsers,
  getTotalEngagements,
  getTotalSpaceMembers,
  getUserActivityMetrics
} from './selectors'

import './index.scss'

import FileSaver from 'file-saver'
import moment from 'moment'
import { usePrevious } from 'react-use'

import { getCurrentSpaceId } from '@/components/App/selectors'
import ErrorConfirmation from '@/components/ErrorConfirmation'
import { Export } from '@/components/Icons'
import useFeatures from '@/components/useFeatures'
import { SPACE_PUBLISHER } from '@/constants/featureToggles'

import ChatMetricsSection from './ChatMetricsSection'
import DateRangeFilters from './DateRangeFilters'
import DeviceMetricsSection from './DeviceMetricsSection'
import IntegrationsSection from './IntegrationsSection'
import messages from './messages'
import PublisherSection from './PublisherSection'
import RelativeDateFilters from './RelativeDateFilters'
import ViewByFilterMap from './RelativeDateFilters/ViewByFilterMap'
import UserActivitySection from './UserActivitySection'
import CustomDateFilterTag from './util/CustomDateFilterTag'
import { generateCsv } from './util/generateCsv'

const errorsToHandle = [
  { type: FETCH_ACTIVE_NOW, data: { detailCode: 'SANF' } },
  { type: FETCH_CONNECTOR_ACTIVITY, data: { detailCode: 'SACAF' } },
  { type: FETCH_CHAT_ACTIVITY, data: { detailCode: 'SPCMF' } }
]

function SpaceAnalyticsPage(props: any) {
  const intl = useIntl()

  const features = useFeatures()

  const [state, _setState] = useState({
    displayFilterOverlay: false,
    isDateRangeQuery: false,
    filterParams: getRelativeDateDefaultState(),
    dateFilterParams: {
      oldest: moment(new Date()).add(-1, 'days').format('YYYY-MM-DD'),
      latest: moment(new Date()).format('YYYY-MM-DD')
    }
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  // componentDidMount
  useEffect(() => {
    fetchData(state.filterParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidUpdate
  const prevProps = usePrevious(props)
  useEffect(() => {
    if (!prevProps) return

    if (prevProps.currentSpaceId !== props.currentSpaceId) {
      fetchData(state.filterParams)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, prevProps])

  function getRelativeDateDefaultState() {
    const defaultViewByFilter = ViewByFilterMap.getViewByRangeItem('30-days')
    return {
      ...defaultViewByFilter,
      // @ts-expect-error
      oldest: defaultViewByFilter.oldest(),
      latest: moment(new Date()).toISOString()
    } as Record<string, string>
  }

  const fetchData = (filterParams: any) => {
    if (filterParams.latest && filterParams.latest) {
      props.actions.fetchActiveNowMetrics()
      props.actions.fetchConnectorActivityMetrics({ oldest: filterParams.oldest, latest: filterParams.latest })
      props.actions.fetchChatMetrics(filterParams)
      props.actions.fetchSystemConnectorsMetrics(filterParams)
      props.actions.fetchEngagementAnalyticsMetrics(filterParams)
    }
  }

  const dataHasLoaded = () => {
    if (props.userActivity.length === 0 && props.engagementOverTime.length === 0) {
      return true
    } else {
      return false
    }
  }

  const onApplyDateRangeFilters = (state: any) => {
    setState(state)
    fetchData(state.filterParams)
  }

  const onChangeAnalyticsViewBy = (state: any) => {
    setState(state)
    fetchData(state.filterParams)
  }

  const onClearDateFilters = (dateFilterParams: any) => {
    setState({ dateFilterParams: { oldest: dateFilterParams.oldest, latest: dateFilterParams.latest } })
  }

  const onCloseFilterOverlay = () => {
    onChangeAnalyticsViewBy({ filterParams: getRelativeDateDefaultState(), displayFilterOverlay: false })
  }

  const onCancelCustomDateFilter = () => {
    onChangeAnalyticsViewBy({
      filterParams: getRelativeDateDefaultState(),
      displayFilterOverlay: false,
      isDateRangeQuery: false
    })
  }

  const exportCsv = () => {
    const blob = generateCsv({ ...props, intl })
    FileSaver.saveAs(blob, `Space_Analytics_${state.filterParams.key}.csv`)
  }

  const {
    currentSpace,
    totalActivity,
    totalEngagements,
    userActivity,
    engagementOverTime,
    totalEngagedNow,
    deviceMetrics,
    totalEngagedUsers,
    previousTotalEngagedUsers,
    previousTotalActivity,
    previousTotalEngagements,
    connectorActivity,
    totalSpaceMembers,
    previousTotalSpaceMembers,
    chatMetrics,
    previousChatMetrics,
    publisherMetrics,
    previousPublisherMetrics
  } = props
  const { filterParams, dateFilterParams, displayFilterOverlay, isDateRangeQuery } = state
  return (
    <Page
      className="space-analytics"
      isLoading={dataHasLoaded() && props.isLoading}
      errorsToHandle={errorsToHandle}
      loadingMessage={<FormattedMessage {...messages.loading} />}
    >
      <>
        <Page.Header title={<FormattedMessage {...messages.title} />} />
        {props.hasError ? (
          <ErrorConfirmation
            error={<FormattedMessage {...messages.errorTitle} />}
            subMessage={<FormattedMessage {...messages.errorMessage} />}
          />
        ) : (
          <Card>
            <div className="export-section" />
            <Card.Header title={<FormattedMessage {...messages.cardTitle} values={{ name: currentSpace.name }} />}>
              <div className="notification-results-options">
                <button className="notification-results-options-item" onClick={exportCsv}>
                  <Export className="notification-results-options-icon" />
                  <FormattedMessage {...messages.exportCSV} />
                </button>
              </div>
            </Card.Header>
            <div className="header-wrapper">
              <div className="sub-section">
                <div className="sub-header">
                  <FormattedMessage {...messages.cardSubTitle} />

                  <a href="https://help.workgrid.com/s/article/Space-Analytics" target="_blank" rel="noreferrer">
                    <span style={{ fontSize: '16px' }}>
                      <FormattedMessage {...messages.cardSubTitleLink} />
                    </span>
                  </a>
                </div>
              </div>
              <div>&nbsp;</div>
              <div className="sub-section right">
                <RelativeDateFilters
                  defaultOption={filterParams.key}
                  onChangeAnalyticsViewBy={onChangeAnalyticsViewBy}
                  onClearDateFilters={onClearDateFilters}
                  dateFilterParams={dateFilterParams}
                />
              </div>
            </div>
            <div className="filter-section">
              <div className="filter-tags">
                {isDateRangeQuery && (
                  <CustomDateFilterTag
                    oldest={filterParams.oldest}
                    latest={filterParams.latest}
                    onClose={onCancelCustomDateFilter}
                  />
                )}
              </div>
              <div className="filter-divider" />
              {isDateRangeQuery && (
                <div className="filter-note">
                  <Warning />
                  <FormattedMessage {...messages.note} />
                  <FormattedMessage {...messages.differenceNote} />
                </div>
              )}
              {displayFilterOverlay && (
                <div className="filter-overlay">
                  <DateRangeFilters
                    onChangeDateRangeFilters={() => {}}
                    dateFilterParams={dateFilterParams}
                    onCloseFilterOverlay={onCloseFilterOverlay}
                    onApplyDateRangeFilters={onApplyDateRangeFilters}
                  />
                </div>
              )}
              <div className="filter-notes"></div>
            </div>
            {userActivity.length > 0 && engagementOverTime.length > 0 ? (
              <UserActivitySection
                totalEngagedUsers={totalEngagedUsers}
                totalActivity={totalActivity}
                totalEngagements={totalEngagements}
                userActivity={userActivity}
                engagementOverTime={engagementOverTime}
                totalEngagedNow={totalEngagedNow}
                chartFormat={filterParams.chartFormat}
                previousTotalEngagedUsers={previousTotalEngagedUsers}
                previousTotalActivity={previousTotalActivity}
                previousTotalEngagements={previousTotalEngagements}
                totalSpaceMembers={totalSpaceMembers}
                previousTotalSpaceMembers={previousTotalSpaceMembers}
              />
            ) : (
              <FormattedMessage {...messages.noActivityMetrics} />
            )}
            <DeviceMetricsSection deviceMetrics={deviceMetrics} />
            <IntegrationsSection connectorActivity={connectorActivity} />
            <ChatMetricsSection chatMetrics={chatMetrics} previousChatMetrics={previousChatMetrics} />
            {features[SPACE_PUBLISHER] && (
              <PublisherSection
                publisherMetrics={publisherMetrics}
                previousPublisherMetrics={previousPublisherMetrics}
                isDateRangeQuery={isDateRangeQuery}
              />
            )}
          </Card>
        )}
      </>
    </Page>
  )
}

SpaceAnalyticsPage.displayName = 'SpaceAnalyticsR1Page'

SpaceAnalyticsPage.propTypes = {
  currentSpace: PropTypes.object.isRequired,
  currentSpaceId: PropTypes.string.isRequired,
  actions: PropTypes.shape({
    fetchActiveNowMetrics: PropTypes.func.isRequired,
    fetchConnectorActivityMetrics: PropTypes.func.isRequired,
    fetchChatMetrics: PropTypes.func.isRequired,
    fetchSystemConnectorsMetrics: PropTypes.func.isRequired,
    fetchEngagementAnalyticsMetrics: PropTypes.func.isRequired
  }).isRequired,
  isSearching: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  hasError: PropTypes.bool.isRequired
}

SpaceAnalyticsPage.defaultProps = {
  isSearching: false
}

const DisconnectedSpaceAnalyticsPage = SpaceAnalyticsPage
export { /** @deprecated */ DisconnectedSpaceAnalyticsPage as SpaceAnalyticsPage }

const mapStateToProps = (state: any) => {
  return {
    currentSpace: getCurrentSpace(state),
    currentSpaceId: getCurrentSpaceId(state),
    userActivity: getUserActivityMetrics(state),
    engagementOverTime: getEngagementOverTimeMetrics(state),
    connectorActivity: getConnectorActivityMetrics(state),
    deviceMetrics: getDeviceMetrics(state),
    totalActiveUsers: getTotalActiveUsers(state),
    totalEngagedUsers: getTotalEngagedUsers(state),
    totalActivity: getTotalActivity(state),
    totalEngagements: getTotalEngagements(state),
    totalEngagedNow: getTotalEngagedNow(state),
    previousTotalActiveUsers: getPreviousTotalActiveUsers(state),
    previousTotalEngagedUsers: getPreviousTotalEngagedUsers(state),
    previousTotalActivity: getPreviousTotalActivity(state),
    previousTotalEngagements: getPreviousTotalEngagements(state),
    totalSpaceMembers: getTotalSpaceMembers(state),
    previousTotalSpaceMembers: getPreviousTotalSpaceMembers(state),
    chatMetrics: getChatMetrics(state),
    previousChatMetrics: getPreviousChatMetrics(state),
    publisherMetrics: getPublisherMetrics(state),
    previousPublisherMetrics: getPreviousPublisherMetrics(state),
    hasError: isRequestError(state, [
      FETCH_ACTIVE_NOW,
      FETCH_CONNECTOR_ACTIVITY,
      FETCH_CHAT_ACTIVITY,
      FETCH_SYSTEM_CONNECTOR_ACTIVITY
    ]),
    isLoading: isRequestPending(state, [
      FETCH_ACTIVE_NOW,
      FETCH_CONNECTOR_ACTIVITY,
      FETCH_CHAT_ACTIVITY,
      FETCH_SYSTEM_CONNECTOR_ACTIVITY
    ])
  }
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    actions: bindActionCreators(
      {
        fetchActiveNowMetrics,
        fetchConnectorActivityMetrics,
        fetchChatMetrics,
        fetchSystemConnectorsMetrics,
        fetchEngagementAnalyticsMetrics
      },
      dispatch
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DisconnectedSpaceAnalyticsPage)
